import type { FC, Ref } from "react";

import { InteractionModeIconText } from "components/materials/page/interaction-mode/InteractionModeIconText";
import {
  INTERACTION_MODE_CHIP_KINDS,
  INTERACTION_MODE_ICONS,
} from "components/materials/page/interaction-mode/helpers";
import { InteractionMode, PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { Chip, type ChipProps } from "mds/components/Chip";
import { Listbox, type ListboxOption } from "mds/components/Listbox";
import { ChevronDownIcon } from "mds/icons";

const InteractionModeChipTrigger = (props: Omit<ChipProps<"button">, "as">) => (
  <Chip as="button" {...props} />
);

const getInteractionModeListboxOptions = (page: PageType): ListboxOption[] => {
  const groupMode =
    page.interaction_mode === InteractionMode.SELF_ASSIGN
      ? InteractionMode.SELF_ASSIGN
      : InteractionMode.ASSIGN_STUDENTS;

  return [InteractionMode.INDIVIDUAL, InteractionMode.SHARED_CLASS, groupMode].map((mode) => ({
    id: mode,
    title: t(`page_interaction_mode_menu.interaction_mode.${mode}`),
    iconStart: INTERACTION_MODE_ICONS[mode],
  }));
};

export type InteractionModeListboxProps = {
  page: PageType;
  onModeChange: (mode: InteractionMode) => void;
  ref?: Ref<HTMLButtonElement>;
  /** Chevron-only hibiscus trigger for DualButton right half; default = full colored chip */
  chevronOnly?: boolean;
};

export const InteractionModeListbox: FC<InteractionModeListboxProps> = ({
  page,
  onModeChange,
  ref,
  chevronOnly = false,
}) => (
  <Listbox
    eventName="Page type updated"
    eventProperties={{ pageTitle: page.title, pageId: page.id }}
    eventPropertyKey="pageInteractionMode"
    items={getInteractionModeListboxOptions(page)}
    selectedValue={page.interaction_mode}
    triggerButton={
      chevronOnly ? (
        <InteractionModeChipTrigger
          aria-label={t("tooltip.change_type_of_activity")}
          className="!px-2"
          kind="hibiscus"
        >
          <ChevronDownIcon />
        </InteractionModeChipTrigger>
      ) : (
        <InteractionModeChipTrigger
          kind={INTERACTION_MODE_CHIP_KINDS[page.interaction_mode]}
          ref={ref}
        >
          <InteractionModeIconText page={page} includeChevron />
        </InteractionModeChipTrigger>
      )
    }
    onSelectionChange={(item) => onModeChange(item.id as InteractionMode)}
  />
);

InteractionModeListbox.displayName = "InteractionModeListbox";
